import React, { useMemo } from 'react';
import { Alert, Modal, ModalBody, ModalFooter, ModalHeader } from 'reactstrap';

import { Button } from '~components';
import Icon, { faExclamationTriangle } from '~components/icon';

import { ClientInvoice } from '~support-at-home/models';

interface ComponentProps {
  invoices: ClientInvoice[];
  targetStatusLabel: string;
  targetStatusId?: string;
  isReversal?: boolean;
  isOpen: boolean;
  isSubmitting?: boolean;
  onClose: () => void;
  onConfirm: (eligibleIds: string[]) => Promise<void> | void;
}

// TAV-2053 bulk-status confirmation. Surfaces the eligibility split before
// firing the bulk mutation: GoCardless-locked rows and rows already in the
// target status are shown as skipped, and the confirm button is disabled when
// no rows are eligible. Filtering the payload client-side mirrors the server's
// own validation so the count shown matches what actually gets updated.
// `isReversal` adds a caution when the action walks the status flow backwards
// (an unlock-only move) so the user knows they're going against the norm.
const EligibilityConfirmDialog: React.FC<ComponentProps> = ({
  invoices,
  targetStatusLabel,
  targetStatusId,
  isReversal = false,
  isOpen,
  isSubmitting = false,
  onClose,
  onConfirm,
}) => {
  const { eligibleIds, lockedCount, unchangedCount } = useMemo(() => {
    const eligible: string[] = [];
    let locked = 0;
    let unchanged = 0;
    for (const invoice of invoices) {
      if (invoice.payment_source === 'gocardless') {
        locked += 1;
      } else if (targetStatusId && invoice.status_id === targetStatusId) {
        // Already in the target status — the server treats this as a no-op,
        // so don't promise to change it.
        unchanged += 1;
      } else {
        eligible.push(invoice.id);
      }
    }
    return { eligibleIds: eligible, lockedCount: locked, unchangedCount: unchanged };
  }, [invoices, targetStatusId]);

  const eligibleCount = eligibleIds.length;
  const canConfirm = eligibleCount > 0 && !isSubmitting;

  const handleConfirm = async () => {
    if (!canConfirm) return;
    await onConfirm(eligibleIds);
  };

  // While the bulk mutation is in flight, lock the modal closed: backdrop
  // clicks, Esc, and the header X all become no-ops. Cancel was already
  // disabled. Without this, the user could close mid-flight and retry,
  // doubling up the request.
  const dismiss = isSubmitting ? undefined : onClose;

  return (
    <Modal
      isOpen={isOpen}
      toggle={dismiss}
      backdrop={isSubmitting ? 'static' : true}
      keyboard={!isSubmitting}
      data-cy="invoice-status-confirm-dialog"
      aria-labelledby="invoice-status-confirm-header"
      labelledBy="invoice-status-confirm-header"
    >
      <ModalHeader toggle={dismiss} id="invoice-status-confirm-header">
        Mark as {targetStatusLabel}
      </ModalHeader>
      <ModalBody>
        {isReversal && eligibleCount > 0 && (
          <Alert
            color="warning"
            className="mb-3"
            data-cy="invoice-status-confirm-reversal-warning"
            data-testid="invoice-status-confirm-reversal-warning"
          >
            <Icon icon={faExclamationTriangle} className="me-2" />
            <span>
              This reverses the normal invoice flow — {eligibleCount === 1 ? 'this invoice' : 'these invoices'} will be
              moved back to {targetStatusLabel}. This is normally a one-way step and is only available with unlock
              permission.
            </span>
          </Alert>
        )}
        <p data-cy="invoice-status-confirm-eligible">
          <strong>{eligibleCount}</strong> {eligibleCount === 1 ? 'invoice' : 'invoices'} will be marked as{' '}
          {targetStatusLabel}.
        </p>
        {lockedCount > 0 && (
          <Alert color="warning" className="mb-3">
            <Icon icon={faExclamationTriangle} className="me-2" />
            <span data-cy="invoice-status-confirm-skipped">
              <strong>{lockedCount}</strong> {lockedCount === 1 ? 'invoice' : 'invoices'} will be skipped — managed by
              GoCardless and cannot be manually changed.
            </span>
          </Alert>
        )}
        {unchangedCount > 0 && (
          <p
            className="text-muted mb-0"
            data-cy="invoice-status-confirm-unchanged"
            data-testid="invoice-status-confirm-unchanged"
          >
            <strong>{unchangedCount}</strong> {unchangedCount === 1 ? 'invoice is' : 'invoices are'} already marked as{' '}
            {targetStatusLabel} and will be skipped.
          </p>
        )}
        {eligibleCount === 0 && <p className="text-muted mb-0">No eligible invoices in the current selection.</p>}
      </ModalBody>
      <ModalFooter>
        <Button
          className="btn-success"
          onClick={handleConfirm}
          disabled={!canConfirm}
          data-cy="invoice-status-confirm-button"
        >
          {isSubmitting ? 'Updating…' : `Mark ${eligibleCount} as ${targetStatusLabel}`}
        </Button>
        <Button color="secondary" onClick={onClose} disabled={isSubmitting} data-cy="invoice-status-cancel-button">
          Cancel
        </Button>
      </ModalFooter>
    </Modal>
  );
};

export default EligibilityConfirmDialog;
